// Definición declarativa de los módulos CRUD sencillos (leads, contacts,
// accounts, products). Alimenta la lista genérica y el formulario genérico.
// Los módulos complejos (deals, quotes, activities) tienen páginas propias.

export type FieldType =
  | "text" | "textarea" | "email" | "phone" | "url"
  | "int" | "currency" | "decimal" | "percent"
  | "date" | "bool" | "enum" | "businessunit";

export interface FieldDef {
  name: string;          // api_name
  label: string;
  type: FieldType;
  required?: boolean;
  options?: readonly string[]; // enum
  section?: string;
  min?: number;
  custom?: boolean;      // campo personalizado → se guarda en custom_data
  optionsFromCustomField?: string; // reutiliza las opciones (picklist) de un campo personalizado por apiName
}

export interface ColumnDef {
  name: string;
  label: string;
  type?: FieldType;
  primary?: boolean;     // enlace a la ficha
  custom?: boolean;      // el valor se lee de custom_data (campo personalizado)
}

export interface ModuleDef {
  key: string;           // path de la API y ruta ("leads")
  singular: string;      // key del objeto en la respuesta de detalle
  titleField: (row: Record<string, unknown>) => string;
  nav: string;           // etiqueta de navegación
  fields: FieldDef[];
  columns: ColumnDef[];
  /** Filtros opcionales de lista (dropdowns) cuyos valores vienen de un endpoint.
   *  El endpoint devuelve un objeto con una lista por `key` (strings o {value,label}). */
  listFilters?: {
    optionsPath: string;
    filters: Array<{ param: string; label: string; key: string }>;
  };
  /** Orden explícito de las secciones en la ficha (las no listadas van al final). */
  sectionOrder?: string[];
}

// ── Listas de enums (Parte 8 / diccionario) ──
export const LEAD_STATUS = ["Nuevo", "Intentando contactar", "Contactado", "Calificado", "No calificado", "Perdido"] as const;
export const LEAD_SOURCE = ["Web", "Referido", "Feria", "Llamada en frío", "Publicidad", "Redes sociales", "Otro"] as const;
export const INDUSTRY = ["Tecnología", "Manufactura", "Retail", "Servicios", "Salud", "Educación", "Construcción", "Finanzas", "Gobierno", "Otro"] as const;
export const RATING = ["Frío", "Tibio", "Caliente"] as const;
export const ACCOUNT_TYPE = ["Cliente", "Prospecto", "Socio", "Competidor", "Proveedor"] as const;

/** Colores de lead_status (11.1) para badges. */
export const LEAD_STATUS_COLOR: Record<string, string> = {
  "Nuevo": "#3B82F6",
  "Intentando contactar": "#F59E0B",
  "Contactado": "#8B5CF6",
  "Calificado": "#10B981",
  "No calificado": "#6B7280",
  "Perdido": "#EF4444",
};

const str = (row: Record<string, unknown>, k: string): string => {
  const v = row[k];
  return v == null ? "" : String(v);
};

export const MODULES: Record<string, ModuleDef> = {
  leads: {
    key: "leads",
    singular: "lead",
    nav: "Leads",
    titleField: (r) => `${str(r, "firstName")} ${str(r, "lastName")}`.trim() || "(sin nombre)",
    columns: [
      { name: "lastName", label: "Nombre", primary: true },
      { name: "company", label: "Empresa" },
      { name: "leadStatus", label: "Estado", type: "enum" },
      { name: "email", label: "Email" },
      { name: "phone", label: "Teléfono" },
      { name: "leadSource", label: "Fuente" },
    ],
    fields: [
      { name: "firstName", label: "Nombre", type: "text", section: "Datos del lead" },
      { name: "lastName", label: "Apellido", type: "text", required: true, section: "Datos del lead" },
      { name: "company", label: "Empresa", type: "text", required: true, section: "Datos del lead" },
      { name: "title", label: "Cargo", type: "text", section: "Datos del lead" },
      { name: "leadStatus", label: "Estado", type: "enum", options: LEAD_STATUS, required: true, section: "Datos del lead" },
      { name: "rating", label: "Calificación", type: "enum", options: RATING, section: "Datos del lead" },
      { name: "email", label: "Email", type: "email", section: "Contacto" },
      { name: "phone", label: "Teléfono", type: "phone", section: "Contacto" },
      { name: "mobile", label: "Móvil", type: "phone", section: "Contacto" },
      { name: "website", label: "Sitio web", type: "url", section: "Contacto" },
      { name: "emailOptOut", label: "No contactar", type: "bool", section: "Contacto" },
      { name: "leadSource", label: "Fuente", type: "enum", options: LEAD_SOURCE, section: "Detalles" },
      { name: "industry", label: "Industria", type: "enum", options: INDUSTRY, section: "Detalles" },
      { name: "noOfEmployees", label: "Nº empleados", type: "int", min: 0, section: "Detalles" },
      { name: "annualRevenue", label: "Ingresos anuales", type: "currency", section: "Detalles" },
      { name: "street", label: "Calle", type: "text", section: "Dirección" },
      { name: "city", label: "Ciudad", type: "text", section: "Dirección" },
      { name: "state", label: "Departamento", type: "text", section: "Dirección" },
      { name: "zipCode", label: "Código postal", type: "text", section: "Dirección" },
      { name: "country", label: "País", type: "text", section: "Dirección" },
      { name: "description", label: "Descripción", type: "textarea", section: "Dirección" },
    ],
  },

  contacts: {
    key: "contacts",
    singular: "contact",
    nav: "Contactos",
    listFilters: { optionsPath: "contacts/filters", filters: [{ param: "grupo", label: "Grupo de interés", key: "grupo" }] },
    titleField: (r) => `${str(r, "firstName")} ${str(r, "lastName")}`.trim() || "(sin nombre)",
    columns: [
      { name: "lastName", label: "Nombre", primary: true },
      { name: "ROL", label: "Rol", custom: true },
      { name: "accountName", label: "Cuenta" },
      { name: "email", label: "Email" },
      { name: "phone", label: "Teléfono" },
      { name: "title", label: "Cargo" },
    ],
    fields: [
      { name: "firstName", label: "Nombre", type: "text", section: "Datos del contacto" },
      { name: "lastName", label: "Apellido", type: "text", required: true, section: "Datos del contacto" },
      { name: "title", label: "Cargo", type: "text", section: "Datos del contacto" },
      { name: "department", label: "Departamento", type: "text", section: "Datos del contacto" },
      { name: "email", label: "Email", type: "email", section: "Contacto" },
      { name: "secondaryEmail", label: "Email secundario", type: "email", section: "Contacto" },
      { name: "phone", label: "Teléfono", type: "phone", section: "Contacto" },
      { name: "mobile", label: "Móvil", type: "phone", section: "Contacto" },
      { name: "leadSource", label: "Fuente", type: "enum", options: LEAD_SOURCE, section: "Contacto" },
      { name: "emailOptOut", label: "No contactar", type: "bool", section: "Contacto" },
      { name: "mailingStreet", label: "Calle", type: "text", section: "Dirección" },
      { name: "mailingCity", label: "Ciudad", type: "text", section: "Dirección" },
      { name: "mailingState", label: "Departamento", type: "text", section: "Dirección" },
      { name: "mailingZip", label: "Código postal", type: "text", section: "Dirección" },
      { name: "mailingCountry", label: "País", type: "text", section: "Dirección" },
      { name: "description", label: "Descripción", type: "textarea", section: "Dirección" },
    ],
  },

  accounts: {
    key: "accounts",
    singular: "account",
    nav: "Cuentas",
    sectionOrder: ["Datos de la cuenta", "Redes sociales", "Detalles", "Dirección de facturación", "Clasificación", "Perfil corporativo", "Financiero"],
    listFilters: {
      optionsPath: "accounts/filters",
      filters: [
        { param: "grupo", label: "Grupo de interés", key: "grupo" },
        { param: "macro", label: "Macrosector", key: "macro" },
        { param: "region", label: "Región", key: "region" },
        { param: "tipo", label: "Tipo", key: "tipo" },
        { param: "comercial", label: "Comercial", key: "comercial" },
      ],
    },
    titleField: (r) => str(r, "accountName") || "(sin nombre)",
    columns: [
      { name: "accountName", label: "Cuenta", primary: true },
      { name: "accountType", label: "Tipo", type: "enum" },
      { name: "phone", label: "Teléfono" },
      { name: "industry", label: "Industria" },
      { name: "website", label: "Sitio web" },
    ],
    fields: [
      { name: "accountName", label: "Nombre de la cuenta", type: "text", required: true, section: "Datos de la cuenta" },
      { name: "accountType", label: "Tipo", type: "enum", options: ACCOUNT_TYPE, section: "Datos de la cuenta" },
      { name: "industry", label: "Industria", type: "enum", options: INDUSTRY, section: "Datos de la cuenta" },
      { name: "taxId", label: "NIT", type: "text", section: "Datos de la cuenta" },
      { name: "phone", label: "Teléfono", type: "phone", section: "Datos de la cuenta" },
      { name: "website", label: "Sitio web", type: "url", section: "Datos de la cuenta" },
      { name: "noOfEmployees", label: "Nº empleados", type: "int", min: 0, section: "Detalles" },
      { name: "annualRevenue", label: "Ingresos anuales", type: "currency", section: "Detalles" },
      { name: "billingStreet", label: "Calle (facturación)", type: "text", section: "Dirección de facturación" },
      { name: "billingCity", label: "Ciudad", type: "text", section: "Dirección de facturación", optionsFromCustomField: "REGION" },
      { name: "billingState", label: "Departamento", type: "text", section: "Dirección de facturación" },
      { name: "billingZip", label: "Código postal", type: "text", section: "Dirección de facturación" },
      { name: "billingCountry", label: "País", type: "text", section: "Dirección de facturación" },
      { name: "description", label: "Descripción", type: "textarea", section: "Dirección de facturación" },
    ],
  },

  terceros: {
    key: "terceros",
    singular: "tercero",
    nav: "Terceros",
    sectionOrder: ["Identificación", "Contacto", "Ubicación", "Clasificación", "Seguimiento", "Notas"],
    listFilters: {
      optionsPath: "terceros/filters",
      filters: [
        { param: "estado", label: "Estado", key: "estado" },
        { param: "servicio", label: "Servicio", key: "servicio" },
        { param: "sector", label: "Sector", key: "sector" },
        { param: "pais", label: "País", key: "pais" },
        { param: "propietario", label: "Propietario", key: "propietario" },
      ],
    },
    titleField: (r) => `${str(r, "nombre")} ${str(r, "apellidos")}`.trim() || str(r, "empresa") || "(sin nombre)",
    columns: [
      { name: "nombre", label: "Nombre", primary: true },
      { name: "empresa", label: "Empresa" },
      { name: "email", label: "Correo" },
      { name: "telefono", label: "Teléfono" },
      { name: "estado", label: "Estado" },
      { name: "pais", label: "País" },
    ],
    fields: [
      { name: "nombre", label: "Nombre", type: "text", section: "Identificación" },
      { name: "apellidos", label: "Apellidos", type: "text", section: "Identificación" },
      { name: "empresa", label: "Empresa", type: "text", section: "Identificación" },
      { name: "cargo", label: "Cargo", type: "text", section: "Identificación" },
      { name: "genero", label: "Género", type: "text", section: "Identificación" },
      { name: "tipoDocumento", label: "Tipo de documento", type: "text", section: "Identificación" },
      { name: "numeroId", label: "Número de ID", type: "text", section: "Identificación" },
      { name: "nifNit", label: "NIF/NIT", type: "text", section: "Identificación" },
      { name: "email", label: "Correo electrónico", type: "email", section: "Contacto" },
      { name: "email2", label: "Correo electrónico 2", type: "email", section: "Contacto" },
      { name: "telefono", label: "Teléfono", type: "phone", section: "Contacto" },
      { name: "telefono2", label: "Teléfono 2", type: "phone", section: "Contacto" },
      { name: "direccion", label: "Dirección", type: "text", section: "Ubicación" },
      { name: "ciudad", label: "Ciudad", type: "text", section: "Ubicación" },
      { name: "pais", label: "País", type: "text", section: "Ubicación" },
      { name: "sector", label: "Sector", type: "text", section: "Clasificación" },
      { name: "servicio", label: "Servicio", type: "text", section: "Clasificación" },
      { name: "anioServicio", label: "Año del servicio", type: "text", section: "Clasificación" },
      { name: "mesServicio", label: "Mes del servicio", type: "text", section: "Clasificación" },
      { name: "origenContacto", label: "Origen contacto", type: "text", section: "Seguimiento" },
      { name: "canalOnline", label: "Canal online", type: "text", section: "Seguimiento" },
      { name: "estado", label: "Estado", type: "text", section: "Seguimiento" },
      { name: "ultimoContacto", label: "Último contacto", type: "date", section: "Seguimiento" },
      { name: "gdpr", label: "Aceptación GDPR", type: "bool", section: "Seguimiento" },
      { name: "propietario", label: "Propietario", type: "text", section: "Seguimiento" },
      { name: "etiquetas", label: "Etiquetas", type: "text", section: "Seguimiento" },
      { name: "descripcion", label: "Descripción", type: "textarea", section: "Notas" },
      { name: "observaciones", label: "Observaciones", type: "textarea", section: "Notas" },
    ],
  },

  products: {
    key: "products",
    singular: "product",
    nav: "Servicios",
    listFilters: {
      optionsPath: "products/filters",
      filters: [{ param: "businessUnitId", label: "Unidad de negocio", key: "businessUnit" }],
    },
    titleField: (r) => str(r, "productName") || "(sin nombre)",
    columns: [
      { name: "productName", label: "Servicio", primary: true },
      { name: "productCode", label: "Código" },
      { name: "unitPrice", label: "Precio", type: "currency" },
      { name: "taxPercent", label: "IVA %", type: "percent" },
      { name: "isActive", label: "Activo", type: "bool" },
    ],
    fields: [
      { name: "productName", label: "Nombre", type: "text", required: true, section: "Servicio" },
      { name: "productCode", label: "Código", type: "text", required: true, section: "Servicio" },
      { name: "businessUnitId", label: "Unidad de negocio", type: "businessunit", section: "Servicio" },
      { name: "category", label: "Categoría", type: "text", section: "Servicio" },
      { name: "unit", label: "Unidad", type: "text", section: "Servicio" },
      { name: "isActive", label: "Activo", type: "bool", section: "Servicio" },
      { name: "unitPrice", label: "Precio unitario", type: "currency", required: true, section: "Precios" },
      { name: "taxPercent", label: "IVA %", type: "percent", section: "Precios" },
      { name: "cost", label: "Costo", type: "currency", section: "Precios" },
      { name: "description", label: "Descripción", type: "textarea", section: "Precios" },
    ],
  },
};
